import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';
import type { ExportTimelineInput } from '@casetrace/export';
import type {
  AcceptInvitationInput,
  AdminCreateUserInput,
  AdminOverviewResult,
  AdminUpdateCaseInput,
  AdminUpdateUserInput,
  Case,
  CaseId,
  CaseMember,
  CaseRole,
  CreateDocumentUploadInput,
  CreateDocumentUploadResult,
  CreateIphoneSmsUploadInput,
  CreateIphoneSmsUploadResult,
  CreatePstUploadInput,
  CreatePstUploadResult,
  GetAttachmentDownloadUrlInput,
  GetAttachmentDownloadUrlResult,
  CreateWhatsappUploadInput,
  CreateWhatsappUploadResult,
  CreateCaseInput,
  DeleteInvitationInput,
  DeleteSourceInput,
  Invitation,
  InviteMemberInput,
  IphoneSmsConversation,
  ListCaseMembersInput,
  SourceId,
  StartImportInput,
  StartIphoneSmsImportInput,
} from '@casetrace/shared';
import { auth, functions, storage } from './firebase';

export function getImporterBaseUrl(): string {
  return (import.meta.env.VITE_IMPORTER_BASE_URL ?? 'http://localhost:8787').replace(/\/$/, '');
}

export type ExportTimelineFile = {
  readonly name: string;
  readonly contentType: string;
  readonly content: string;
};

export type ExportTimelineResult = {
  readonly exportId: string;
  readonly exportedAt: string;
  readonly caseNavn: string;
  readonly saksnummer: string;
  readonly counts: {
    readonly events: number;
    readonly sourceItems: number;
    readonly sources: number;
  };
  readonly files: readonly ExportTimelineFile[];
};

export type MyCase = {
  readonly case: Case;
  readonly role: CaseRole;
};

export type CaseMemberListItem = CaseMember & {
  readonly email?: string;
  readonly displayName?: string;
};

export type ListCaseMembersResult = {
  readonly members: CaseMemberListItem[];
  readonly pendingInvitations: Invitation[];
};

export async function createCase(input: CreateCaseInput): Promise<{ readonly caseId: string }> {
  const callable = httpsCallable<CreateCaseInput, { readonly caseId: string }>(
    functions,
    'createCase',
  );
  return (await callable(input)).data;
}

export async function inviteMember(
  input: InviteMemberInput,
): Promise<{ readonly invitationId: string }> {
  const callable = httpsCallable<InviteMemberInput, { readonly invitationId: string }>(
    functions,
    'inviteMember',
  );
  return (await callable(input)).data;
}

export async function acceptInvitation(
  input: AcceptInvitationInput,
): Promise<{ readonly caseId: string }> {
  const callable = httpsCallable<AcceptInvitationInput, { readonly caseId: string }>(
    functions,
    'acceptInvitation',
  );
  return (await callable(input)).data;
}

export async function deleteInvitation(
  input: DeleteInvitationInput,
): Promise<{ readonly ok: boolean }> {
  const callable = httpsCallable<DeleteInvitationInput, { readonly ok: boolean }>(
    functions,
    'deleteInvitation',
  );
  return (await callable(input)).data;
}

export async function listMyCases(): Promise<MyCase[]> {
  const callable = httpsCallable<void, MyCase[]>(functions, 'listMyCases');
  return (await callable()).data;
}

export async function listAdminOverview(): Promise<AdminOverviewResult> {
  const callable = httpsCallable<void, AdminOverviewResult>(functions, 'listAdminOverview');
  return (await callable()).data;
}

export async function adminCreateUser(
  input: AdminCreateUserInput,
): Promise<{ readonly uid: string }> {
  const callable = httpsCallable<AdminCreateUserInput, { readonly uid: string }>(
    functions,
    'provisionUser',
  );
  return (await callable(input)).data;
}

export async function adminUpdateUser(
  input: AdminUpdateUserInput,
): Promise<{ readonly ok: boolean }> {
  const callable = httpsCallable<AdminUpdateUserInput, { readonly ok: boolean }>(
    functions,
    'updateAdminUser',
  );
  return (await callable(input)).data;
}

export async function adminUpdateCase(
  input: AdminUpdateCaseInput,
): Promise<{ readonly ok: boolean }> {
  const callable = httpsCallable<AdminUpdateCaseInput, { readonly ok: boolean }>(
    functions,
    'updateAdminCase',
  );
  await callable(input);
  return { ok: true };
}

export async function listCaseMembers(input: ListCaseMembersInput): Promise<ListCaseMembersResult> {
  const callable = httpsCallable<ListCaseMembersInput, ListCaseMembersResult>(
    functions,
    'listCaseMembers',
  );
  return (await callable(input)).data;
}

export async function listMyInvitations(): Promise<Invitation[]> {
  const callable = httpsCallable<void, Invitation[]>(functions, 'listMyInvitations');
  return (await callable()).data;
}

export async function exportTimeline(input: ExportTimelineInput): Promise<ExportTimelineResult> {
  const callable = httpsCallable<ExportTimelineInput, ExportTimelineResult>(
    functions,
    'exportTimeline',
  );
  return (await callable(input)).data;
}

export async function startImport(input: StartImportInput): Promise<{ readonly jobId: string }> {
  const callable = httpsCallable<StartImportInput, { readonly jobId: string }>(
    functions,
    'startImport',
  );
  return (await callable(input)).data;
}

export async function createWhatsappUpload(
  input: CreateWhatsappUploadInput,
): Promise<CreateWhatsappUploadResult> {
  const callable = httpsCallable<CreateWhatsappUploadInput, CreateWhatsappUploadResult>(
    functions,
    'createWhatsappUpload',
  );
  return (await callable(input)).data;
}

export async function createPstUpload(input: CreatePstUploadInput): Promise<CreatePstUploadResult> {
  const callable = httpsCallable<CreatePstUploadInput, CreatePstUploadResult>(
    functions,
    'createPstUpload',
  );
  return (await callable(input)).data;
}

export async function createDocumentUpload(
  input: CreateDocumentUploadInput,
): Promise<CreateDocumentUploadResult> {
  const callable = httpsCallable<CreateDocumentUploadInput, CreateDocumentUploadResult>(
    functions,
    'createDocumentUpload',
  );
  return (await callable(input)).data;
}

export async function uploadSourceFile(input: {
  caseId: string;
  sourceId: string;
  uploadObjectPath: string;
  file: File;
  contentType?: string;
}): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const uploadTask = uploadBytesResumable(ref(storage, input.uploadObjectPath), input.file, {
      contentType: input.contentType ?? 'application/zip',
    });

    uploadTask.on('state_changed', undefined, reject, () => resolve());
  });
}

export async function uploadDocumentFile(input: {
  caseId: CaseId;
  sourceId: SourceId;
  file: File;
}): Promise<void> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Du må være innlogget for å laste opp filer.');
  }

  const base = getImporterBaseUrl();
  const res = await fetch(
    `${base}/uploads/${input.caseId}/${input.sourceId}/${encodeURIComponent(input.file.name)}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'content-type': input.file.type || 'application/octet-stream',
      },
      body: input.file,
    },
  );

  if (!res.ok) {
    throw new Error(`Opplasting feilet med status ${res.status}`);
  }
}

export async function createIphoneSmsUpload(
  input: CreateIphoneSmsUploadInput,
): Promise<CreateIphoneSmsUploadResult> {
  const callable = httpsCallable<CreateIphoneSmsUploadInput, CreateIphoneSmsUploadResult>(
    functions,
    'createIphoneSmsUpload',
  );
  return (await callable(input)).data;
}

export type PreviewIphoneSmsResult = {
  readonly conversations: IphoneSmsConversation[];
  readonly totalMessages: number;
  readonly totalConversations: number;
};

export async function previewIphoneSms(input: {
  caseId: string;
  sourceId: string;
}): Promise<PreviewIphoneSmsResult> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Du må være innlogget for å forhåndsvise import.');
  }

  const base = getImporterBaseUrl();
  const res = await fetch(`${base}/iphone-sms/preview/${input.caseId}/${input.sourceId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const payload: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const serverMessage =
      payload &&
      typeof payload === 'object' &&
      'error' in payload &&
      typeof (payload as { error: unknown }).error === 'string'
        ? (payload as { error: string }).error
        : `Forhåndsvisning feilet med status ${res.status}`;
    throw new Error(serverMessage);
  }

  return payload as PreviewIphoneSmsResult;
}

export async function startIphoneSmsImport(
  input: StartIphoneSmsImportInput,
): Promise<{ readonly jobId: string }> {
  const callable = httpsCallable<StartIphoneSmsImportInput, { readonly jobId: string }>(
    functions,
    'startIphoneSmsImport',
  );
  return (await callable(input)).data;
}

export async function deleteSource(input: DeleteSourceInput): Promise<{ readonly ok: boolean }> {
  const callable = httpsCallable<DeleteSourceInput, { readonly ok: boolean }>(
    functions,
    'deleteSource',
  );
  return (await callable(input)).data;
}

export async function getAttachmentDownloadUrl(
  input: GetAttachmentDownloadUrlInput,
): Promise<GetAttachmentDownloadUrlResult> {
  const callable = httpsCallable<GetAttachmentDownloadUrlInput, GetAttachmentDownloadUrlResult>(
    functions,
    'getAttachmentDownloadUrl',
  );
  return (
    await callable({
      ...input,
      disposition: input.disposition ?? 'inline',
    })
  ).data;
}
